import { useState } from "react";
import { useForm, FieldValues } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import FestivalService, { Festival } from "@/app/_services/FestivalService";
import { festivalSchema } from "./FestivalSchema";
import { toast } from "@/app/_utils/toast";

type FormData = yup.InferType<typeof festivalSchema>;

export function useFestivalFormFlow({
  defaultFestival,
  onSaved,
}: {
  defaultFestival?: Festival | null;
  onSaved: () => void;
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(festivalSchema),
    values: defaultFestival
      ? {
          title: defaultFestival.title,
          description: defaultFestival.description ?? "",
          show_ids: defaultFestival.shows?.map((s) => s.id) ?? [],
        }
      : {
          title: "",
          description: "",
          show_ids: [],
        },
  });

  const handleSave = handleSubmit(async (fields: FieldValues) => {
    setIsSaving(true);
    const payload = selectedImage ? { ...fields, image: selectedImage } : fields;
    try {
      if (defaultFestival) {
        await FestivalService.updateFestivalAsync(defaultFestival.id, payload as any);
        toast.success("Festival updated");
      } else {
        await FestivalService.createFestivalAsync(payload as any);
        toast.success("Festival created");
        reset({ title: "", description: "", show_ids: [] });
      }
      setSelectedImage(null);
      onSaved();
    } catch (e) {
      toast.error("Failed to save festival");
    } finally {
      setIsSaving(false);
    }
  });

  function discardChanges() {
    reset();
    setSelectedImage(null);
  }

  return {
    control,
    errors,
    isSaving,
    selectedImage,
    setSelectedImage,
    handleSave,
    isDirty: isDirty || selectedImage !== null,
    discardChanges,
  };
}
